import React from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Icon,
  IconButton,
  Tooltip,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  Chip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { PageHeader } from "../../components/PageHeader/PageHeader";
import { EmptyState } from "../../../../components/EmptyState/EmptyState";
import { configService } from "../../services/config.service";
import { DeleteConfirmationDialog } from "../../components/DeleteConfirmationDialog/DeleteConfirmationDialog";
import { PermissionTreeView } from "../../components/PermissionsManager/PermissionTreeView/PermissionTreeView";
import { getPermissionGroups } from "../../utils/permissions.utils";
import type { PermissionGroup } from "../../utils/permissions.utils";
import { AppTable } from "../../../../components/AppTable";
import { AppSearch } from "../../../../components/AppSearch";
import { TableColumn, WithId, FilterGroup } from "../../../../app.interface";

interface Role {
  _id: string;
  name: string;
  description: string;
  permissions: string[];
  is_system_role: boolean;
  created_at: string;
}

// Add type for table row data
type RoleTableData = Role & WithId;

export const RolesManager: React.FC = () => {
  const navigate = useNavigate();
  const [roles, setRoles] = React.useState<Role[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [selectedRole, setSelectedRole] = React.useState<Role | null>(null);
  const [deleting, setDeleting] = React.useState(false);
  const [quickViewRole, setQuickViewRole] = React.useState<Role | null>(null);
  const [permissionGroups, setPermissionGroups] = React.useState<
    PermissionGroup[]
  >([]);
  const [sortField, setSortField] = React.useState<string>("name");
  const [sortOrder, setSortOrder] = React.useState<"asc" | "desc">("asc");
  const [filters, setFilters] = React.useState<Record<string, any>>({});

  const filterGroups: FilterGroup[] = [
    {
      id: "type",
      label: "Role Type",
      type: "select",
      icon: "badge",
      description: "Filter roles by type",
      options: [
        {
          id: "system",
          label: "System Roles",
          value: "system",
          icon: "admin_panel_settings",
          color: "#0891B2",
          description: "Built-in system roles",
        },
        {
          id: "custom",
          label: "Custom Roles",
          value: "custom",
          icon: "person",
          color: "#059669",
          description: "User-created roles",
        },
      ],
    },
    {
      id: "sortBy",
      label: "Sort By",
      type: "select",
      icon: "sort",
      description: "Sort roles by field",
      options: [
        {
          id: "name_asc",
          label: "Name (A-Z)",
          value: "name_asc",
          icon: "arrow_upward",
        },
        {
          id: "name_desc",
          label: "Name (Z-A)",
          value: "name_desc",
          icon: "arrow_downward",
        },
        {
          id: "created_asc",
          label: "Created (Oldest)",
          value: "created_asc",
          icon: "schedule",
        },
        {
          id: "created_desc",
          label: "Created (Newest)",
          value: "created_desc",
          icon: "update",
        },
      ],
    },
  ];

  const MasterColumns: TableColumn[] = [
    {
      id: "name",
      header: "Role Name",
      field: "name",
      filterField: "name",
      type: "text",
      sortable: true,
      width: "200px",
      renderAction: (row: any) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography fontWeight={600}>{row.name}</Typography>
          {row.is_system_role && (
            <Chip
              label="System"
              size="small"
              color="primary"
              variant="outlined"
              sx={{ borderRadius: 0.5, height: 20 }}
            />
          )}
        </Box>
      ),
    },

    {
      id: "permissions",
      header: "Permissions",
      field: "permissions",
      type: "action",
      width: "150px",
      renderAction: (row: any) => (
        <Button
          size="small"
          variant="text"
          onClick={(e) => {
            e.stopPropagation();
            handleQuickView(row as Role);
          }}
          sx={{
            color: row.is_system_role ? "primary.main" : "text.secondary",
            "&:hover": {
              bgcolor: alpha("#0891B2", 0.04),
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Icon sx={{ fontSize: "1rem" }}>verified</Icon>
            <Typography variant="caption" sx={{ fontWeight: 500 }}>
              {row.permissions.length} permissions
            </Typography>
          </Box>
        </Button>
      ),
    },
    {
      id: "created_at",
      header: "Created At",
      field: "created_at",
      type: "datetime",
      width: "180px",
      sortable: true,
    },
    {
      id: "actions",
      header: "",
      field: "actions",
      type: "action",
      width: "100px",
      renderAction: (row: any) => (
        <div className="flex gap-2 justify-end">
          <Tooltip
            title={
              row.is_system_role ? "System roles cannot be edited" : "Edit Role"
            }
            arrow
            placement="top"
          >
            <span>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEdit(row as Role);
                }}
                disabled={row.is_system_role}
                sx={{
                  width: 34,
                  height: 34,
                  bgcolor: (theme) => alpha("#0891B2", 0.08),
                  color: "#0891B2",
                  backdropFilter: "blur(8px)",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-1px)",
                    bgcolor: (theme) => alpha("#0891B2", 0.12),
                    boxShadow: "0 4px 8px rgba(8, 145, 178, 0.15)",
                  },
                  "&:active": {
                    transform: "translateY(0)",
                  },
                  "&.Mui-disabled": {
                    bgcolor: "action.disabledBackground",
                  },
                }}
              >
                <Icon sx={{ fontSize: 18 }}>edit_outlined</Icon>
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip
            title={
              row.is_system_role
                ? "System roles cannot be deleted"
                : "Delete Role"
            }
            arrow
            placement="top"
          >
            <span>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(row as Role);
                }}
                disabled={row.is_system_role}
                sx={{
                  width: 34,
                  height: 34,
                  bgcolor: (theme) => alpha("#DC2626", 0.08),
                  color: "#DC2626",
                  backdropFilter: "blur(8px)",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-1px)",
                    bgcolor: (theme) => alpha("#DC2626", 0.12),
                    boxShadow: "0 4px 8px rgba(220, 38, 38, 0.15)",
                  },
                  "&:active": {
                    transform: "translateY(0)",
                  },
                  "&.Mui-disabled": {
                    bgcolor: "action.disabledBackground",
                  },
                }}
              >
                <Icon sx={{ fontSize: 18 }}>delete_outlined</Icon>
              </IconButton>
            </span>
          </Tooltip>
        </div>
      ),
    },
  ];

  React.useEffect(() => {
    loadRoles();
    loadPermissions();
  }, [search, filters, sortField, sortOrder]);

  const loadRoles = async () => {
    try {
      setLoading(true);
      const response = await configService.listRoles({
        searchQuery: search,
        ...filters,
        sortField,
        sortOrder,
      });
      const mappedRoles = response.data.map((doc) => ({
        _id: doc._id,
        name: doc.name || "",
        description: doc.description || "",
        permissions: doc.permissions || [],
        is_system_role: doc.is_system_role || false,
        created_at: doc.created_at || new Date().toISOString(),
      }));
      setRoles(mappedRoles);
    } catch (error) {
      toast.error("Failed to load roles");
      console.error("Error loading roles:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadPermissions = async () => {
    try {
      const response = await configService.listPermissions({
        pageSize: 10000,
        pageIndex: 0,
      });
      const groups = getPermissionGroups(response.data);
      setPermissionGroups(groups);
    } catch (error) {
      console.error("Error loading permissions:", error);
    }
  };

  const handleEdit = (role: Role) => {
    if (role.is_system_role) {
      toast.warning("System roles cannot be edited");
      return;
    }
    navigate(`/business/configuration/roles/${role._id}/edit`, {
      state: { role, permissionGroups },
    });
  };

  const handleDelete = async (role: Role) => {
    if (role.is_system_role) {
      toast.warning("System roles cannot be deleted");
      return;
    }
    setSelectedRole(role);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setSelectedRole(null);
    setDeleteDialogOpen(false);
  };

  const handleConfirmDelete = async () => {
    if (!selectedRole) return;
    try {
      setDeleting(true);
      await configService.deleteRole(selectedRole._id);
      toast.success("Role deleted successfully");
      handleCloseDeleteDialog();
      loadRoles();
    } catch (error) {
      toast.error("Failed to delete role");
      console.error("Error deleting role:", error);
    } finally {
      setDeleting(false);
    }
  };

  const handleQuickView = (role: Role) => {
    setQuickViewRole(role);
  };

  const handleCloseQuickView = () => {
    setQuickViewRole(null);
  };

  const handleSort = (field: string) => {
    if (field === sortField) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="Role Management"
        subtitle="Define and manage user roles and permissions"
        icon="admin_panel_settings"
        actions={
          <Button
            variant="contained"
            startIcon={<Icon>add</Icon>}
            onClick={() => navigate("/business/configuration/roles/new")}
            sx={{
              bgcolor: "#0891B2",
              "&:hover": {
                bgcolor: "#0E7490",
              },
            }}
          >
            Create Role
          </Button>
        }
      />

      <Paper
        sx={{
          p: 3,
          mb: 3,
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          border: "1px solid",
          borderColor: alpha("#0891B2", 0.1),
        }}
        elevation={0}
      >
        <AppSearch
          placeholder="Search roles by name or description..."
          maxWidth="100%"
          onSearch={setSearch}
          onFilterChange={setFilters}
          filterGroups={filterGroups}
          defaultFilters={filters}
        />
      </Paper>

      <Paper
        sx={{
          p: 3,
          mb: 3,
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          border: "1px solid",
          borderColor: alpha("#0891B2", 0.1),
        }}
        elevation={0}
      >
        <AppTable
          data={roles}
          allowHeaderWrap
          columns={MasterColumns}
          variant="default"
          sortConfig={{ field: sortField, direction: sortOrder }}
          onSort={handleSort}
          loading={loading}
          header={{
            title: "Roles List",
            subtitle: "Your current list of roles",
          }}
          selectable={false}
          onSelectionChange={(selected) => {
            console.log("Selected:", selected);
          }}
          styleOptions={{
            bordered: true,
            columnSeparators: true,
            headerBackground: alpha("#0891B2", 0.02),
            rowHoverBackground: alpha("#0891B2", 0.04),
            borderColor: alpha("#0891B2", 0.1),
          }}
          toolbarActions={{
            refresh: {
              label: "Refresh List",
              is_disabled: loading,
              onClick: loadRoles,
            },
          }}
        />
      </Paper>

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleConfirmDelete}
        title="Delete Role"
        itemName={selectedRole?.name || ""}
        itemDescription={selectedRole?.description || ""}
        loading={deleting}
        icon="admin_panel_settings"
      />

      <Dialog
        open={!!quickViewRole}
        onClose={handleCloseQuickView}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            width: 600,
            maxWidth: "100%",
            borderRadius: 2,
            overflow: "hidden",
            borderLeft: "1px solid",
            borderColor: alpha("#0891B2", 0.1),
          },
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            background: "#0891B2",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          }}
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "rgba(255,255,255,0.1)",
              }}
            >
              <Icon>
                {quickViewRole?.is_system_role
                  ? "admin_panel_settings"
                  : "badge"}
              </Icon>
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                Role Details
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                View role permissions and details
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleCloseQuickView}
            size="small"
            sx={{
              color: "white",
              "&:hover": {
                bgcolor: "rgba(255,255,255,0.1)",
              },
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </Box>

        <Box sx={{ p: 3 }}>
          <Box
            sx={{
              p: 2,
              borderRadius: 1,
              bgcolor: alpha("#0891B2", 0.04),
              border: "1px solid",
              borderColor: alpha("#0891B2", 0.1),
              mb: 3,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {quickViewRole?.name}
              </Typography>
              {quickViewRole?.is_system_role && (
                <Chip
                  label="System"
                  size="small"
                  sx={{
                    borderRadius: 1,
                    height: 22,
                    bgcolor: alpha("#0891B2", 0.08),
                    color: "#0891B2",
                    border: "1px solid",
                    borderColor: alpha("#0891B2", 0.2),
                    "& .MuiChip-label": {
                      px: 1,
                      fontSize: "0.75rem",
                      fontWeight: 600,
                    },
                  }}
                />
              )}
            </Box>
            {quickViewRole?.description && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ lineHeight: 1.5 }}
              >
                {quickViewRole.description}
              </Typography>
            )}
          </Box>

          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 2,
              }}
            >
              <Icon sx={{ color: "text.secondary", fontSize: 20 }}>
                verified
              </Icon>
              <Typography
                variant="subtitle2"
                sx={{
                  color: "text.primary",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  fontSize: "0.75rem",
                }}
              >
                Assigned Permissions ({quickViewRole?.permissions.length})
              </Typography>
            </Box>

            <Box
              sx={{
                maxHeight: "400px",
                overflowY: "auto",
                "&::-webkit-scrollbar": {
                  width: "8px",
                  height: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  background: alpha("#0891B2", 0.04),
                  borderRadius: "4px",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: alpha("#0891B2", 0.1),
                  borderRadius: "4px",
                  "&:hover": {
                    background: alpha("#0891B2", 0.15),
                  },
                },
              }}
            >
              {quickViewRole &&
                permissionGroups.map((group, index) => (
                  <Box
                    key={group.name}
                    sx={{
                      mb: index < permissionGroups.length - 1 ? 2 : 0,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        p: 1.5,
                        bgcolor: alpha("#0891B2", 0.04),
                        borderTopLeftRadius: 1,
                        borderTopRightRadius: 1,
                        borderBottom: "2px solid",
                        borderColor: alpha("#0891B2", 0.1),
                      }}
                    >
                      <Icon sx={{ color: "#0891B2", fontSize: 18 }}>
                        {group.icon || "folder"}
                      </Icon>
                      <Typography
                        sx={{
                          fontSize: "0.875rem",
                          fontWeight: 600,
                          color: "text.primary",
                        }}
                      >
                        {group.name}
                      </Typography>
                      <Typography
                        sx={{
                          ml: "auto",
                          fontSize: "0.75rem",
                          color: "text.secondary",
                          bgcolor: alpha("#0891B2", 0.08),
                          px: 1,
                          py: 0.25,
                          borderRadius: 0.75,
                        }}
                      >
                        {
                          group.permissions.filter((p) =>
                            quickViewRole.permissions.includes(p.id)
                          ).length
                        }{" "}
                        permissions
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        p: 1,
                        border: "1px solid",
                        borderColor: alpha("#0891B2", 0.1),
                        borderTop: "none",
                        borderBottomLeftRadius: 1,
                        borderBottomRightRadius: 1,
                      }}
                    >
                      {group.permissions.map((permission) => {
                        const isGranted = quickViewRole.permissions.includes(
                          permission.id
                        );
                        return (
                          <Box
                            key={permission.id}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                              p: 1,
                              borderRadius: 1,
                              transition: "all 0.2s ease-in-out",
                              "&:hover": {
                                bgcolor: alpha("#0891B2", 0.04),
                              },
                            }}
                          >
                            <Icon
                              sx={{
                                color: isGranted ? "#059669" : "text.disabled",
                                fontSize: 18,
                              }}
                            >
                              {isGranted
                                ? "check_circle"
                                : "radio_button_unchecked"}
                            </Icon>
                            <Box>
                              <Typography
                                sx={{
                                  fontSize: "0.875rem",
                                  fontWeight: 500,
                                  color: isGranted
                                    ? "text.primary"
                                    : "text.secondary",
                                  mb: 0.25,
                                }}
                              >
                                {permission.name}
                              </Typography>
                              {permission.description && (
                                <Typography
                                  sx={{
                                    fontSize: "0.75rem",
                                    color: "text.secondary",
                                    lineHeight: 1.3,
                                  }}
                                >
                                  {permission.description}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                ))}
            </Box>
          </Box>

          <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<Icon>edit</Icon>}
              onClick={() => {
                handleEdit(quickViewRole!);
                handleCloseQuickView();
              }}
              disabled={quickViewRole?.is_system_role}
              sx={{
                borderColor: "#0891B2",
                color: "#0891B2",
                "&:hover": {
                  borderColor: "#0E7490",
                  bgcolor: alpha("#0891B2", 0.04),
                },
                "&.Mui-disabled": {
                  borderColor: "action.disabledBackground",
                },
              }}
            >
              Edit Role
            </Button>
            <Button
              fullWidth
              variant="outlined"
              color="error"
              startIcon={<Icon>delete</Icon>}
              onClick={() => {
                handleDelete(quickViewRole!);
                handleCloseQuickView();
              }}
              disabled={quickViewRole?.is_system_role}
              sx={{
                borderColor: "#DC2626",
                color: "#DC2626",
                "&:hover": {
                  borderColor: "#B91C1C",
                  bgcolor: alpha("#DC2626", 0.04),
                },
                "&.Mui-disabled": {
                  borderColor: "action.disabledBackground",
                },
              }}
            >
              Delete Role
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
};
